import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { badgeClass } from "@/lib/config";
import { AlertOctagon } from "lucide-react";

const SEVERITIES = ["All", "CRITICAL", "HIGH", "MEDIUM", "LOW"];
const STATUSES = ["All", "New", "Acknowledged", "Assigned", "Investigating", "Resolved", "Dismissed"];
const WORK_STATUSES = ["New", "Acknowledged", "Assigned", "Investigating", "Resolved", "Dismissed"];

export default function ErrorCenter() {
  const [items, setItems] = useState([]);
  const [sev, setSev] = useState("All");
  const [status, setStatus] = useState("All");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/errors?page_size=300${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      let list = data.items || [];
      if (sev !== "All") list = list.filter((e) => e.severity === sev);
      if (status !== "All") list = list.filter((e) => (e.status || "New") === status);
      setItems(list);
    } catch { toast.error("Could not load errors."); }
  }, [sev, status, q]);
  useEffect(() => { load(); }, [load]);

  const open = (e) => { setActive(e); setEdit({ status: e.status, severity: e.severity, owner: e.owner || "", root_cause: e.root_cause || "", resolution: e.resolution || "" }); };

  const save = async () => {
    try {
      await api.post(`/errors/${active.id}/status`, edit);
      toast.success("Error updated.");
      setActive(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update."); }
  };

  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Error Center" subtitle="Centralized tracking of system and user-reported issues. No stack traces or secrets are shown to normal users." />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input data-testid="error-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by Error ID, message, module…" className="h-9 w-72 bg-white text-sm" />
        <Select value={sev} onValueChange={setSev}><SelectTrigger data-testid="error-sev-filter" className="h-9 w-40 bg-white text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s === "All" ? "All severities" : s}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="error-status-filter" className="h-9 w-44 bg-white text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "All" ? "All statuses" : s}</SelectItem>)}</SelectContent></Select>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-10 text-center text-mutedsea" data-testid="error-empty">
          <AlertOctagon className="h-8 w-8 mx-auto mb-2 text-seagreen" /> No errors match. The system is healthy.
        </div>
      ) : (
        <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="error-list">
          <table className="w-full text-sm">
            <thead className="bg-cloud text-mutedsea text-xs uppercase tracking-wide">
              <tr><th className="text-left px-3 py-2">Error ID</th><th className="text-left px-3 py-2">Severity</th><th className="text-left px-3 py-2">Module</th><th className="text-left px-3 py-2">Message</th><th className="text-left px-3 py-2">Count</th><th className="text-left px-3 py-2">Status</th></tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} onClick={() => open(e)} data-testid={`error-row-${e.id}`} className="border-t border-border hover:bg-ice cursor-pointer">
                  <td className="px-3 py-2 font-mono text-xs text-atlantic">{e.error_id}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={badgeClass(e.severity)}>{e.severity}</Badge></td>
                  <td className="px-3 py-2 text-slate">{e.module || e.category}</td>
                  <td className="px-3 py-2 text-slate max-w-md truncate">{e.message}</td>
                  <td className="px-3 py-2 text-mutedsea">{e.occurrence_count || 1}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={badgeClass(e.status)}>{e.status || "New"}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-white" data-testid="error-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">Error {active?.error_id}</DialogTitle>
            <DialogDescription className="text-xs text-mutedsea">Correlation {active?.correlation_id} · first seen {active?.first_seen?.slice(0, 16).replace("T", " ")} · last seen {active?.last_seen?.slice(0, 16).replace("T", " ")}</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="py-2 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-mutedsea">Category</span><div className="text-slate">{active.category}</div></div>
                <div><span className="text-mutedsea">Operation</span><div className="text-slate">{active.operation || "—"}</div></div>
                <div><span className="text-mutedsea">Route</span><div className="text-slate break-all">{active.route || "—"}</div></div>
                <div><span className="text-mutedsea">Occurrences</span><div className="text-slate">{active.occurrence_count || 1}</div></div>
              </div>
              <div><span className="text-xs text-mutedsea">Message</span><div className="bg-cloud rounded-sm p-2 text-slate">{active.message}</div></div>
              {active.what_trying && <div><span className="text-xs text-mutedsea">User was trying to</span><div className="text-slate">{active.what_trying}</div></div>}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-mutedsea mb-1 block">Status</label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}><SelectTrigger data-testid="error-edit-status" className="bg-white text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{WORK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                <div><label className="text-xs text-mutedsea mb-1 block">Owner</label>
                  <Input data-testid="error-edit-owner" value={edit.owner} onChange={(e) => setEdit({ ...edit, owner: e.target.value })} className="bg-white text-sm" /></div>
              </div>
              <div><label className="text-xs text-mutedsea mb-1 block">Root cause</label><Textarea data-testid="error-edit-rootcause" rows={2} value={edit.root_cause} onChange={(e) => setEdit({ ...edit, root_cause: e.target.value })} className="bg-white text-sm" /></div>
              <div><label className="text-xs text-mutedsea mb-1 block">Resolution</label><Textarea data-testid="error-edit-resolution" rows={2} value={edit.resolution} onChange={(e) => setEdit({ ...edit, resolution: e.target.value })} className="bg-white text-sm" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Close</Button>
            <Button data-testid="error-save" onClick={save} className="bg-navy hover:bg-atlantic text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
